import React, { PropsWithChildren } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Icon } from "../Icon";
import type { IconName } from "@/types/icons";
import { LinkData } from "@/types";
import Link from "next/link";
import { Wysiwyg } from "../Wysiwyg";
import { Heading } from "../Heading";

const contentCardVariants = cva(
  "flex flex-col p-md rounded-lg focus-outline-light",
  {
    variants: {
      color: {
        yellow: "bg-yellow text-black",
        "purple-medium": "bg-purple-medium text-white",
        "purple-dark": "bg-purple-dark text-white",
      },
      size: {
        default: "aspect-4/5 gap-sm justify-end",
        small: "gap-xs",
      },
    },
    defaultVariants: {
      color: "yellow",
      size: "default",
    },
  }
);

const headlineVariants = cva("mb-2", {
  variants: {
    color: {
      yellow: "text-black",
      "purple-medium": "text-white",
      "purple-dark": "text-white",
    },
  },
  defaultVariants: {
    color: "yellow",
  },
});

const labelVariants = cva("text-sm font-bold", {
  variants: {
    color: {
      yellow: "text-black",
      "purple-medium": "text-white",
      "purple-dark": "text-white",
    },
  },
  defaultVariants: {
    color: "yellow",
  },
});

const textVariants = cva("", {
  variants: {
    color: {
      yellow: "text-black childLinkDark",
      "purple-medium": "text-white childLinkLight",
      "purple-dark": "text-white childLinkLight",
    },
    size: {
      default: "text-md",
      small: "text-sm",
    },
  },
  defaultVariants: {
    color: "yellow",
    size: "default",
  },
});

export interface ContentCardProps extends VariantProps<
  typeof contentCardVariants
> {
  icon?: IconName;
  color?: "yellow" | "purple-medium" | "purple-dark";
  size?: "default" | "small";
  headline: string | React.ReactNode;
  label?: string | React.ReactNode;
  text: string | React.ReactNode;
  className?: string;
  link?: LinkData;
}

const CardWrapper: React.FC<
  { link?: LinkData; className: string } & PropsWithChildren
> = ({ link, className, children }) => {
  if (!!link)
    return (
      <Link
        aria-label={link.ariaLabel ?? link.label}
        href={link.href}
        target={link.target}
        rel={link.rel}
        className={className}
      >
        {children}
      </Link>
    );
  return <div className={className}>{children}</div>;
};

function htmlContainsHref(html: string): boolean {
  return /<a\b[^>]*\bhref\s*=\s*(['"]).*?\1/i.test(html);
}

export const ContentCard: React.FC<ContentCardProps> = ({
  icon,
  color,
  size = "default",
  headline,
  label,
  text,
  className = "",
  link,
}) => {
  return (
    <CardWrapper
      link={htmlContainsHref(text as string) ? undefined : link}
      className={contentCardVariants({ color, size, className })}
    >
      {icon && (
        <div className="mb-2">
          <Icon
            name={icon}
            size={size === "default" ? 48 : 24}
            decorative
            aria-hidden="true"
          />
        </div>
      )}
      <Heading
        tag={"h3"}
        style={size === "default" ? "h4" : "h5"}
        className={headlineVariants({ color })}
      >
        {headline}
      </Heading>
      {label && <p className={labelVariants({ color })}>{label}</p>}
      {typeof text === "string" ? (
        <Wysiwyg
          className={textVariants({ color, size })}
          html={text}
          mode="body"
        />
      ) : (
        <p className={textVariants({ color, size })}>{text}</p>
      )}
    </CardWrapper>
  );
};
